import base64 from 'base-64';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import toast from 'react-hot-toast';
import { getUserTypeOptions } from '../../../../constants/constants';
import { AccountStatusEnum } from '../../../../constants/enums';
import { blankOptions } from '../../../../constants/options.constant';
import { useStore } from '../../../../contexts/store-provider';
import {
  getUserId,
  getUserType,
} from '../../../../helpers/localstorage-helper';
import { IAddUser, IUpdateUser } from '../../../../models/forms/IAddUpdateUser';
import { IFormModalProps } from '../../../../models/forms/IForm';
import { formatMessage } from '../../../../translations/format-message';
import AddEditBaseUserForm from '../../forms/base-user/add-edit-base-user-form';
import './add-edit-base-user.css';

const AddEditBaseUser = (props: IFormModalProps) => {
  const { userStore, factoryStore, processStore } = useStore();
  const {
    AddUserService,
    UpdateUserService,
    GetUserByIdService,
    resetAddUpdateUserState,
    resetUserDetail,
    addUpdateUserState,
    userDetails,
    userState,
    allUserAssignedMulitSelectOptions,
  } = userStore;
  const {
    GetAllFactoriesService,
    allAvailableFactories,
    getAllFactoryState,
  } = factoryStore;

  const {
    GetAllProcessService,
    allProcessMulitSelectOptions,
    getAllProcessState,
  } = processStore;
  const initialValues: any = userDetails;

  /**
   * The useEffect Hook lets you perform side effects in function component.
   * The function we pass to the useEffect Hook runs when the component is mounted.
   * Factories and processes are only fetched if not already loaded in the store,
   * to avoid re-triggering the parent page's observer on allAvailableFactories
   * which would call the user list API and show a background loader unnecessarily.
   */
  useEffect(() => {
    if (!getAllFactoryState.success) {
      GetAllFactoriesService();
    }
    if (!getAllProcessState.success) {
      GetAllProcessService();
    }
    if (props.id > 0) {
      GetUserByIdService(props.id);
    }
  }, []);

  useEffect(() => {
    if (addUpdateUserState.success) {
      if (props.id === -1) toast.success(formatMessage('added_success'));
      else toast.success(formatMessage('updated_success'));
      onModalClosed();
    }
  }, [addUpdateUserState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "userDetailError" of the useEffect changed.
   */
  useEffect(() => {
    if (userState.error) {
      toast.error(formatMessage(userState.error));
    }
  }, [userState.error]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateUserError" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateUserState.error) {
      toast.error(formatMessage(addUpdateUserState.error));
      resetAddUpdateUserState();
    }
  }, [addUpdateUserState.error]);

  const onSubmitForm = (values: any) => {
    if (props.id < 0) {
      var obj: IAddUser = {
        TenantId: values.TenantId,
        Email: values.Email.trim(),
        Password: base64.encode(values.Password),
        UserName: values.UserName.trim(),
        UserType: Number(values.UserType),
        AccountStatus: values.AccountStatus,
        Comment: values.Comment.trim(),
        UserId: values.UserId,
        IsReserved: false,
        ProcessId: values.ProcessId,
      };
      AddUserService(obj);
    } else {
      var updateObj: IUpdateUser = {
        Id: Number(props.id),
        Email:
          values.Email && values.AccountStatus === AccountStatusEnum.Active
            ? initialValues.Email.trim()
            : values.Email,
        UserName: values.UserName.trim(),
        UserType: Number(values.UserType),
        AccountStatus: values.AccountStatus,
        Comment: values.Comment.trim(),
        ProcessId: values.ProcessId,
      };
      UpdateUserService(props.id, updateObj);
    }
  };

  const onModalClosed = () => {
    resetUserDetail();
    props.modalClosed();
  };

  return (
    <React.Fragment>
      <AddEditBaseUserForm
        initialValues={initialValues}
        id={props.id}
        userTypeOptions={getUserTypeOptions(getUserType())}
        tenantOptions={allAvailableFactories}
        submitHandler={onSubmitForm}
        onModalClosed={onModalClosed}
        isReserved={
          initialValues.IsReserved || initialValues.Id === Number(getUserId())
        }
        isLoading={getAllProcessState.inProgress || addUpdateUserState.inProgress || userState.inProgress}
        addUpdateUserLoading={false}
        isUserTypeSuperAdminOrDIDManager={false}
        tenantBlankOptions={blankOptions}
        allProcessOptionsNew={allProcessMulitSelectOptions}
        allUserAssignedMulitSelectOptions={allUserAssignedMulitSelectOptions}
      />
    </React.Fragment>
  );
};

export default observer(AddEditBaseUser);
